import { useEffect, useState } from "react";
import Input from "../../../form/Input";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import Textarea from "../../../form/Textarea";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { countryList } from "../../../../services/countryService";
import { countries } from "../../../../db/countries";
import { loadDataOnScroll } from "../../../../helpers";
import {
  includeTaxOptions,
  scopeOptions,
  taxTypes,
} from "../../../../util/Flag";
import { taxAddEdit } from "../../../../services/taxService";

const validation = {
  name: { required: true, message: "Tax type is required" },
  region: { required: true, message: "Region is required" },
  country: { required: true, message: "Country is required" },
  currency: { required: true, message: "Currency is required" },
  scope: { required: true, message: "Scope is required" },
  rate: {
    required: true,
    message: "Rate is required",
    validate: (value) =>
      (Number(value) >= 0 && Number(value) <= 100) ||
      "Rate must be between 0 and 100",
  },
  includeTax: { required: true, message: "Include tax flag is required" },
  description: {}, // optional
};

const ManageTax = ({ getResponse = () => {}, close = () => {}, data }) => {
  const [countryKeyword, setCountryKeyword] = useState("");
  const [countryFilter, setCountryFilter] = useState({
    limit: 20,
    page: 1,
  });
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({ validationRules: validation });

  useEffect(() => {
    if (data?._id) {
      setFieldsValue({
        name: data?.name || "",
        region: data?.region || "",
        country: data?.country || "",
        scope: data?.scope || "",
        currency: data?.currency || "",
        rate: data?.rate || "",
        includeTax: data?.includeTax || "exclusive",
        description: data?.description || "",
      });
    }
  }, [data]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        name: val?.name || "",
        region: val?.region || "",
        country: val?.country || "",
        currency: val?.currency,
        scope: val?.scope,
        rate: val?.rate || "",
        includeTax: val?.includeTax || "exclusive",
        description: val?.description || "",
        ...(data?._id && { id: data?._id }),
      };

      return await taxAddEdit({
        ...payload,
        ...(data?._id && { id: data?._id }),
      }); // Make sure this is defined in your service
    },
    onSuccess: (res) => {
      getResponse(res); // handle success response
      return res;
    },
    onError: (err) => {
      console.error("Tax Rate Submission Error:", err);
    },
  });

  const { data: country } = useQuery({
    queryKey: ["country", countryKeyword, countryFilter],
    queryFn: async () => {
      const res = await countryList({
        ...countryFilter,
        ...(countryKeyword && { keyword: countryKeyword }),
      });
      if (countryFilter?.page > 1) {
        return {
          ...res?.docs,
          data: [...(country?.data || []), ...res?.docs?.data],
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-12 gap-4 p-4">
        <div className="col-span-6">
          <Select
            label="Name"
            placeholder="Select Name"
            selectedValue={values?.name}
            dropdownData={taxTypes}
            onChange={(val) =>
              handleChange({ target: { name: "name", value: val } })
            }
            {...formValidate(errors, "name")}
          />
        </div>

        <div className="col-span-6">
          <Input
            label="Region"
            inputPlaceholder={"Select Region"}
            inputName="region"
            value={values?.region || ""}
            onChange={handleChange}
            {...formValidate(errors, "region")}
          />
        </div>

        <div className="col-span-6">
          <Select
            label={"Country"}
            isSearch
            onSearchHandler={setCountryKeyword}
            placeholder={"Select Country"}
            onChange={(e) => {
              handleChange({ target: { name: "country", value: e } });
            }}
            selectedValue={values?.country}
            dropdownData={[
              {
                name: values?.country,
                value: values?.country,
                image: countries?.find(
                  (cun) => cun?.name?.common.toUpperCase() === values?.country
                )?.flags?.svg,
              },
              ...((Array.isArray(country?.data) ? country.data : []).map(
                (cn) => ({
                  name: cn?.name,
                  value: cn?.name?.toUpperCase(),
                  image: countries?.find((c) => c?.cca3 === cn?.iso3)?.flags
                    ?.svg,
                })
              ) || []),
            ]}
            onScroll={(e) => {
              loadDataOnScroll({
                event: e,
                metadata: country?.metadata,
                setData: setCountryFilter,
              });
            }}
            {...formValidate(errors, "country")}
          />
          {/* <Select
            label="Region"
            placeholder="Select region..."
            selectedValue={values?.region}
            dropdownData={regions}
            onChange={(val) =>
              handleChange({ target: { name: "region", value: val } })
            }
            {...formValidate(errors, "region")}
          /> */}
        </div>

        <div className="col-span-6">
          <Input
            label="Rate"
            inputPlaceholder="0"
            inputName="rate"
            inputSuffix="%"
            value={values?.rate || ""}
            onChange={handleChange}
            {...formValidate(errors, "rate")}
          />
        </div>

        <div className="col-span-6">
          <Input
            label="Currency"
            inputPlaceholder={"Enter Currency"}
            inputName="currency"
            value={values?.currency || ""}
            onChange={handleChange}
            {...formValidate(errors, "currency")}
          />
        </div>

        <div className="col-span-6">
          <Select
            label="Include tax in price"
            placeholder="Select Your Choise"
            selectedValue={values?.includeTax}
            dropdownData={includeTaxOptions}
            onChange={(val) =>
              handleChange({ target: { name: "includeTax", value: val } })
            }
            {...formValidate(errors, "includeTax")}
          />
        </div>

        <div className="col-span-6">
          <Select
            label="Scope"
            placeholder="Select Scope"
            selectedValue={values?.scope}
            dropdownData={scopeOptions}
            onChange={(val) =>
              handleChange({ target: { name: "scope", value: val } })
            }
            {...formValidate(errors, "scope")}
          />
        </div>

        <div className="col-span-12">
          <Textarea
            label="Description"
            inputPlaceholder="Add description"
            inputClasses={"!h-40"}
            inputName="description"
            inputValue={values?.description || ""}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={"Save"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-primary !bg-transparent !text-primary hover:!bg-primary hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel={"Cancel"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-primary !bg-transparent !text-primary hover:!bg-primary hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageTax;
